import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Alert,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircleRounded';
import CameraAltIcon from '@mui/icons-material/CameraAltRounded';
import { closetAPI } from '../utils/api';
import DetectedItemsList from '../components/DetectedItemsList';
import { bulkAddClosetItems } from '../utils/bulkAdd';

const steps = ['Upload Photos', 'Analyzing', 'Review Items', 'Done'];

const ScanCloset = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [allDetectedItems, setAllDetectedItems] = useState([]);
  const [scanWarnings, setScanWarnings] = useState([]);
  const [scanRetention, setScanRetention] = useState({ sourceCount: 0, days: 14 });
  const [referenceUploadIndex, setReferenceUploadIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [addedCount, setAddedCount] = useState(0);

  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const photoPreviewsRef = useRef([]);

  useEffect(() => {
    photoPreviewsRef.current = photoPreviews;
  }, [photoPreviews]);

  useEffect(() => {
    const abortController = abortControllerRef;
    const timeoutId = timeoutIdRef;
    const previews = photoPreviewsRef;
    return () => {
      isMountedRef.current = false;
      if (abortController.current) {
        abortController.current.abort();
      }
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      previews.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 5 - photos.length;
    const newFiles = files.slice(0, remaining);

    if (files.length > remaining) {
      setAlert({
        open: true,
        message: `You can upload up to 5 photos. Only the first ${remaining} were added.`,
        severity: 'warning',
      });
    }

    const newPreviews = newFiles.map((f) => URL.createObjectURL(f));
    setPhotos((prev) => [...prev, ...newFiles]);
    setPhotoPreviews((prev) => [...prev, ...newPreviews]);
  };

  const handleRemovePhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    setPhotoPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStartScan = async () => {
    setActiveStep(1);
    setLoading(true);
    setScanWarnings([]);

    try {
      const formData = new FormData();
      photos.forEach((photo) => formData.append('files', photo));

      const response = await closetAPI.scanCloset(formData);

      if (!isMountedRef.current) return;

      const data = response.data.data;
      const items = (data.items || []).map((item) => ({
        ...item,
        name: item.name || item.type || item.category || 'Item',
        source_photo: item.source_photo != null ? item.source_photo : 0,
      }));
      const warnings = (data.errors || []).map((error) =>
        typeof error === 'string' ? error : error.message
      ).filter(Boolean);

      setAllDetectedItems(items);
      setScanWarnings(warnings);
      setScanRetention({
        sourceCount: data.source_photos?.length || 0,
        days: data.source_retention_days || 14,
      });

      if (items.length === 0) {
        setActiveStep(0);
        setAlert({
          open: true,
          message: warnings[0] || 'No clear clothing items were found. Try brighter, closer photos.',
          severity: 'warning',
        });
        return;
      }

      setActiveStep(2);

      setAlert({
        open: true,
        message: data.status === 'partial'
          ? `Found ${items.length} items. ${data.photos_failed} photo(s) need another try.`
          : `Found ${items.length} items across ${data.photos_processed} photo(s)!`,
        severity: data.status === 'partial' ? 'warning' : 'success',
      });
    } catch (err) {
      console.error('Error scanning closet:', err);
      if (!isMountedRef.current) return;

      setAlert({
        open: true,
        message: err.response?.data?.error || 'Failed to scan photos. Please try again.',
        severity: 'error',
      });
      setActiveStep(0);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleEditItem = (index, field, value) => {
    setAllDetectedItems((prev) =>
      prev.map((item, i) => (i === index ? {
        ...item,
        [field]: value,
        user_corrected_fields: [...new Set([...(item.user_corrected_fields || []), field])],
      } : item))
    );
  };

  const handleRemoveItem = (index) => {
    setAllDetectedItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReplaceReference = async (index, file) => {
    setReferenceUploadIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await closetAPI.uploadImage(formData);
      if (!isMountedRef.current) return;
      setAllDetectedItems((prev) => prev.map((item, itemIndex) => (
        itemIndex === index ? {
          ...item,
          image_url: response.data.url,
          image_blob_name: response.data.blob_name || null,
          visual_reference_status: 'ready',
          reference_warning: null,
          source_photo_id: null,
        } : item
      )));
      setAlert({
        open: true,
        message: 'Garment reference replaced.',
        severity: 'success',
      });
    } catch (err) {
      setAlert({
        open: true,
        message: err.response?.data?.error || 'Could not upload that garment photo.',
        severity: 'error',
      });
    } finally {
      if (isMountedRef.current) setReferenceUploadIndex(null);
    }
  };

  const handleRemoveReference = (index) => {
    setAllDetectedItems((prev) => prev.map((item, itemIndex) => (
      itemIndex === index ? {
        ...item,
        image_url: null,
        image_blob_name: null,
        visual_reference_status: 'description_only',
        reference_warning: 'This item will use its written description for future visualizations.',
        source_photo_id: null,
      } : item
    )));
  };

  const handleBulkAdd = async () => {
    setLoading(true);
    try {
      const result = await bulkAddClosetItems(allDetectedItems, abortControllerRef, timeoutIdRef);

      if (!isMountedRef.current) return;

      setAddedCount(result.items_added || allDetectedItems.length);
      setActiveStep(3);

      let message = `Successfully added ${result.items_added} items to your closet!`;
      if (result.errors && result.errors.length > 0) {
        message += ` (${result.errors.length} failed)`;
      }
      setAlert({
        open: true,
        message,
        severity: result.errors && result.errors.length > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      console.error('Error adding scanned items:', err);
      if (!isMountedRef.current) return;

      let errorMessage = 'Failed to add items. Please try again.';
      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out. Please try with fewer items or check your connection.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }

      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleAlertClose = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.82)',
                cursor: 'pointer',
                transition: 'transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(14, 123, 255, 0.06)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                style={{ display: 'none' }}
                id="scan-photo-upload"
                disabled={photos.length >= 5}
              />
              <label htmlFor="scan-photo-upload">
                <Box sx={{ cursor: photos.length >= 5 ? 'default' : 'pointer' }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Upload Photos of Your Closet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Select up to 5 photos ({5 - photos.length} remaining)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    PNG, JPG, GIF up to 5MB each
                  </Typography>
                </Box>
              </label>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
              Tips: Take photos of your closet shelves, hanging clothes, or drawers
            </Typography>

            {photos.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Selected Photos ({photos.length}/5)
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {photoPreviews.map((preview, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'relative',
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <img
                        src={preview}
                        alt={`Closet preview ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemovePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          width: 24,
                          height: 24,
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              className="btn-scan"
              onClick={handleStartScan}
              disabled={photos.length === 0}
              fullWidth
              startIcon={<CameraAltIcon />}
              sx={{
                mt: 3,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                borderRadius: 3,
              }}
            >
              Start Scanning
            </Button>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CameraAltIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {`Analyzing ${photos.length} photo(s)...`}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Our AI is analyzing your photos to detect clothing items...
            </Typography>
            <LinearProgress sx={{ maxWidth: 400, mx: 'auto', borderRadius: 2 }} />
          </Box>
        );

      case 2:
        return (
          <Box>
            {scanRetention.sourceCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {`We keep ${scanRetention.sourceCount} original scan photo(s) for up to ${scanRetention.days} days for recovery, then delete them automatically. Reviewed garment crops stay with saved closet items. You can delete originals sooner from My Closet.`}
              </Alert>
            )}
            {scanWarnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {scanWarnings.join(' ')} Detected items from successful photos are ready to review.
              </Alert>
            )}
            <DetectedItemsList
              items={allDetectedItems}
              onEditItem={handleEditItem}
              onRemoveItem={handleRemoveItem}
              onBulkAdd={handleBulkAdd}
              onReplaceReference={handleReplaceReference}
              onRemoveReference={handleRemoveReference}
              referenceUploadIndex={referenceUploadIndex}
              loading={loading || referenceUploadIndex !== null}
              showSourcePhoto={true}
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
              Successfully added {addedCount} items!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your closet has been updated with the scanned items.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/closet')}
              sx={{ py: 1.5, px: 4, fontSize: '1rem', fontWeight: 'bold', borderRadius: 3 }}
            >
              Go to My Closet
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background:
            'radial-gradient(280px 160px at 90% -20%, rgba(124, 58, 237, 0.2), transparent 70%), linear-gradient(135deg, rgba(124, 58, 237, 0.14), rgba(168, 85, 247, 0.08))',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Scan My Closet
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Upload photos of your closet and let AI detect and add all your clothing items at once.
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </Paper>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ScanCloset;
